import { useEffect, useState } from 'react';

// Линия под фиксированным хедером (px): секция считается «текущей», когда её верх ушёл выше неё.
// Совпадает по смыслу со scroll-margin-top секций (~80) + небольшой буфер.
const ACTIVE_LINE = 100;

// Scroll-spy: id «текущей» секции — той, чей верх последним прошёл под хедер (наибольший top среди
// секций выше линии ACTIVE_LINE). Активный пункт меню переключается при прокрутке мышью.
// enabled=false (не на главной) → null. Считаем по rAF на scroll/resize (точнее, чем полосы IO).
export function useScrollSpy(ids: readonly string[], enabled: boolean): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || ids.length === 0) {
      setActive(null);
      return;
    }
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) {
      return;
    }

    let frame = 0;

    function compute(): void {
      frame = 0;
      // Среди секций, чей верх уже выше линии (top <= ACTIVE_LINE), берём самую нижнюю — её top
      // максимален (ближе всего к линии снизу). Это секция, которая сейчас стоит под хедером.
      let current: string | null = null;
      let bestTop = -Infinity;
      for (const el of elements) {
        const top = el.getBoundingClientRect().top;
        if (top <= ACTIVE_LINE && top > bestTop) {
          bestTop = top;
          current = el.id;
        }
      }
      // Выше первой секции (герой) ни одна не прошла линию → активного пункта нет.
      setActive(current);
    }

    function onScroll(): void {
      if (frame === 0) {
        frame = window.requestAnimationFrame(compute);
      }
    }

    compute(); // начальное состояние (на случай загрузки уже проскролленной страницы)
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [ids, enabled]);

  return active;
}
